import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import CheckIcon from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FileAttachmentCategory, ShiftAdjustmentType } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Chip } from '~components/Chip/Chip';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { NewTimeFormField } from '~components/FormFields/NewTimeFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import ScanningOverlay from '~components/ScanningOverlay/ScanningOverlay';
import { FeatureFlags } from '~constants/featureFlags';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { trackApprovalBatch } from '~hooks/useAnalytics/trackApprovals';
import { DriverDay } from '~hooks/useDriverDays/models';
import { useDriverDays } from '~hooks/useDriverDays/useDriverDays';
import { useDrivers } from '~hooks/useDrivers/useDrivers';
import { useFileAttachment } from '~hooks/useFileAttachment/useFileAttachment';
import useResourceUsageLogs from '~hooks/useResourceUsageLogs/useResourceUsageLogs';
import { Timesheet } from '~hooks/useTimesheets/models';
import useTimesheets from '~hooks/useTimesheets/useTimesheets';
import { canSetDriverOffDuty } from '~pages/Approvals/DriverDay/canSetDriverOffDuty';
import { useApprovalsQueryContext } from '~pages/Approvals/DriverDay/contexts/ApprovalsQueryContext';
import TimesheetDrawer from '~pages/Approvals/shared/TimesheetDrawer/TimesheetDrawer';
import {
  fileAttachmentToListItem,
  TimesheetListItem,
  timesheetToListItem,
} from '~pages/Approvals/shared/timesheetListItem';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat, minutesToDecimalHours } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  AddOnChargeDTO,
  ResourceUsageLogDTO,
  schemaHelpers,
  ShiftDetailsSchema,
} from '../../DriverPay/helpers';
import ApprovalActionsMenu, { ApprovedChip } from './ApprovalActionsMenu';
import ChargesTable from './ChargesTable';

const emDash = '-';

const convertMinutesToHours = (minutes: number): number => {
  return minutes / 60;
};
const formatMinutesToHoursString = (minutes: number): string =>
  `${convertMinutesToHours(minutes).toFixed(2)}h`;

const calculateAdjustmentMinutes = (
  startedShiftAt: Date | null,
  endedShiftAt: Date | null,
  totalHrs: number,
) => {
  if (!startedShiftAt || !endedShiftAt) return 0;
  const actualMinutes = dayjs(endedShiftAt).diff(dayjs(startedShiftAt), 'minute', false);
  const delta = totalHrs * 60 - actualMinutes;
  return delta;
};

const calculateTotalHrs = (startedShiftAt: Date, endedShiftAt: Date) => {
  return Math.abs(dayjs(endedShiftAt).diff(dayjs(startedShiftAt), 'hour', true));
};

const setDateWithoutSeconds = (date: Dayjs | Date | string | null): Date | null => {
  if (!date) return null;
  const newDate = dayjs.isDayjs(date) ? date.toDate() : new Date(date);
  newDate.setSeconds(0, 0);
  return newDate;
};

// AI vision extraction is async with no realtime channel, so after a new-model upload we
// poll the created timesheet until it is scanned, then prefill the shift times.
const SCAN_POLL_INTERVAL_MS = 5000; // check scan status every 5s
const MAX_SCAN_POLL_ATTEMPTS = 12; // stop after ~1 min so a stuck extraction never polls forever

// Build a shift Date from the AI-extracted calendar date (authoritative) plus the
// time-of-day of an extracted timestamp, so a time-only or a full-datetime extraction
// both land on the correct day.
const combineShiftDateAndTime = (
  shiftDate: Dayjs | null | undefined,
  time: Dayjs | null | undefined,
): Date | null => {
  if (!time) {
    return null;
  }
  const base = shiftDate ?? time;
  return setDateWithoutSeconds(base.hour(time.hour()).minute(time.minute()));
};

type ShiftSummaryFormData = {
  shiftTimeInHours: number;
  externalId: string | null;
  overrideReason: ShiftAdjustmentType | null;
  shiftStart: Date | null;
  shiftEnd: Date | null;
};

type ChargesFormData = {
  addOnCharges: AddOnChargeDTO[];
  resourceUsageLogs: ResourceUsageLogDTO[];
};

const maxFileSize = 10;

export const ShiftDetails = observer(function ShiftDetails({
  driverDay,
  firstJobStartAt,
  jobsIds,
  onDriverDayChange,
}: {
  driverDay: DriverDay;
  firstJobStartAt: Dayjs;
  jobsIds: string[];
  onDriverDayChange: (driverDay: DriverDay) => void;
}) {
  const theme = useTheme();
  const { addOnsStore, toasterStore, driverDayStore, userStore } = useStores();
  const { getAllAddOnChargesByDriverDay } = useAddOns();
  const { getResourceUsageLogsByDriverDay, batchUpdateResourceUsageLogsByDriverDay } =
    useResourceUsageLogs();
  const [isEditingShiftSummary, setIsEditingShiftSummary] = useState(false);
  const [isEditingCharges, setIsEditingCharges] = useState(false);
  const {
    updateDriverDay,
    approveDriverDay,
    unapproveDriverDay,
    getDriverDayPhasesTypeahead,
    getDriverDayById,
  } = useDriverDays();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { setDriverStateToOffDuty } = useDrivers();
  const driverCanBeSetOffDuty = canSetDriverOffDuty({
    driverCompanyId: driverDay.driver.company?.id,
    driverIsShared: Boolean(driverDay.driver.companyShare),
    pickedCompanyId: companyId,
  });
  const { batchUpdateAddOnChargesByDriverDay } = useAddOns();
  const { refetchApprovals } = useApprovalsQueryContext();
  const {
    createFileAttachmentOnDriverDay,
    updateFileAttachmentById,
    getFileAttachmentByDriverDayId,
    getAllFileAttachmentsByJobId,
    deleteFileAttachmentById,
  } = useFileAttachment();
  const useNewTimesheet = useFeatureFlag({
    featureFlagKey: FeatureFlags.useNewTimesheet,
  });
  const { getTimesheetsOrEmpty, createTimesheet, deleteTimesheetById, getTimesheetById } =
    useTimesheets();
  const [allTimesheets, setAllTimesheets] = useState<TimesheetListItem[]>([]);
  const [isTimesheetLoading, setIsTimesheetLoading] = useState(false);
  const [pendingScanTimesheetId, setPendingScanTimesheetId] = useState<string | null>(
    null,
  );
  const [isTimesheetDeleting, setIsTimesheetDeleting] = useState(false);
  const [isTimesheetDeleteDialogOpen, setIsTimesheetDeleteDialogOpen] = useState(false);
  const [toBeDeletedItem, setToBeDeletedItem] = useState<TimesheetListItem | null>(null);
  const [showAllList, setShowAllList] = useState(false);
  const [drawerTimesheet, setDrawerTimesheet] = useState<Timesheet | null>(null);
  // The list rows are normalized `TimesheetListItem`s; keep the full `Timesheet` records
  // (new-model rows only) so a click can open the drawer without a refetch. Keyed by the
  // list-item key (`timesheet:<id>`).
  const timesheetsByKeyRef = useRef<Map<string, Timesheet>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoadingDriverDay, setIsLoadingDriverDay] = useState(false);

  useEffect(() => {
    getAllAddOnChargesByDriverDay(companyId, driverDay.id);
    getResourceUsageLogsByDriverDay(driverDay.id);
    getDriverDayPhasesTypeahead({ driverDayId: driverDay.id });
  }, [driverDay.id, companyId]);

  useEffect(() => {
    const rtuTimestamp = driverDayStore.rtuDriverDayUpdates[driverDay.id];
    if (rtuTimestamp && !isEditingShiftSummary && !isEditingCharges) {
      getResourceUsageLogsByDriverDay(driverDay.id);
    }
  }, [
    driverDayStore.rtuDriverDayUpdates[driverDay.id],
    driverDay.id,
    isEditingShiftSummary,
    isEditingCharges,
  ]);

  const userPermissions = userStore.getPermissions();

  const addOns = useMemo(() => {
    return addOnsStore.addOnChargesByDriverDayId[driverDay.id] ?? [];
  }, [driverDay.id, addOnsStore.addOnChargesByDriverDayId]);

  const resourceUsageLogs = useMemo(() => {
    return addOnsStore.resourceUsageLogsByDriverDayId[driverDay.id] ?? [];
  }, [driverDay.id, addOnsStore.resourceUsageLogsByDriverDayId]);

  const hasCharges = useMemo(
    () => addOns.length + resourceUsageLogs.length > 0,
    [addOns, resourceUsageLogs],
  );

  const phasesOptions = useMemo(() => {
    return driverDayStore.phaseTypeheadsByDriverDayID[driverDay.id] ?? [];
  }, [driverDay.id, driverDayStore.phaseTypeheadsByDriverDayID]);

  const effectiveShiftStart =
    driverDay.adjustedStartedShiftAt ?? driverDay.startedShiftAt;
  const effectiveShiftEnd = driverDay.adjustedEndedShiftAt ?? driverDay.endedShiftAt;
  const normalizedEffectiveShiftStart =
    setDateWithoutSeconds(effectiveShiftStart) ?? null;
  const normalizedEffectiveShiftEnd = setDateWithoutSeconds(effectiveShiftEnd) ?? null;

  const formMethods = useForm({
    resolver: yupResolver(ShiftDetailsSchema),
    defaultValues: {
      shiftTimeInHours: parseFloat(
        convertMinutesToHours(driverDay.totalShiftMinutes ?? 0).toFixed(2),
      ),
      overrideReason: driverDay.shiftAdjustmentType ?? null,
      externalId: driverDay.externalId ?? null,
      addOnCharges: addOns.map((addOnCharge) => {
        return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
      }),
      resourceUsageLogs: resourceUsageLogs.map((resourceUsageLog) => {
        return schemaHelpers.resourceUsageLogToResourceUsageLogDTO(resourceUsageLog);
      }),
      shiftStart: normalizedEffectiveShiftStart,
      shiftEnd: normalizedEffectiveShiftEnd,
    },
    mode: 'all',
    delayError: 500,
  });

  const {
    setValue,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    control,
    watch,
  } = formMethods;

  const watchedShiftStart = watch('shiftStart');
  const watchedShiftEnd = watch('shiftEnd');

  // While editing, the date captions under the time fields should reflect the form value
  // (e.g. the AI-prefilled shift date) rather than the driver-day's original date.
  const displayShiftStart = isEditingShiftSummary
    ? watchedShiftStart
    : effectiveShiftStart;
  const displayShiftEnd = isEditingShiftSummary ? watchedShiftEnd : effectiveShiftEnd;

  const getTimesheets = async (options?: { silent?: boolean }) => {
    // A silent refresh (e.g. after a drawer close) keeps the current rows on screen and
    // skips the spinner, so the list does not flash empty when nothing changed.
    if (!options?.silent) {
      setIsTimesheetLoading(true);
    }
    try {
      const legacyResults = await Promise.all([
        getFileAttachmentByDriverDayId(driverDay.id),
        ...jobsIds.map((jobId) => getAllFileAttachmentsByJobId(jobId)),
      ]);
      const legacyItems = legacyResults
        .flatMap((data) => data ?? [])
        .filter((file) => file.category === 'Timesheet')
        .map(fileAttachmentToListItem);

      if (useNewTimesheet && companyId) {
        // The backend inherits driver_day_id onto job-linked timesheets, so the
        // driver_day_id filter returns both shift- and job-linked new records. Dual-read
        // keeps any pre-existing legacy attachments visible during the transition.
        const newTimesheets = await getTimesheetsOrEmpty(companyId, {
          driverDayId: driverDay.id,
        });
        timesheetsByKeyRef.current = new Map(
          newTimesheets.map((timesheet) => [`timesheet:${timesheet.id}`, timesheet]),
        );
        setAllTimesheets([...newTimesheets.map(timesheetToListItem), ...legacyItems]);
      } else {
        timesheetsByKeyRef.current = new Map();
        setAllTimesheets(legacyItems);
      }
    } finally {
      setIsTimesheetLoading(false);
    }
  };

  useEffect(() => {
    getTimesheets();
  }, [driverDay.id, jobsIds, companyId, useNewTimesheet]);

  useEffect(() => {
    if (watchedShiftStart && watchedShiftEnd && isDirty) {
      const newWatchedShiftStart = new Date(watchedShiftStart);
      newWatchedShiftStart.setSeconds(0, 0);
      const newWatchedShiftEnd = new Date(watchedShiftEnd);
      newWatchedShiftEnd.setSeconds(0, 0);
      const totalMinutes = calculateTotalHrs(newWatchedShiftStart, newWatchedShiftEnd);
      setValue('shiftTimeInHours', parseFloat(totalMinutes.toFixed(2)));
    }
  }, [watchedShiftStart?.toString(), watchedShiftEnd?.toString()]);

  useEffect(() => {
    // Only update form values when not editing to avoid overwriting user changes
    if (!isEditingShiftSummary) {
      setValue(
        'shiftTimeInHours',
        parseFloat(convertMinutesToHours(driverDay.totalShiftMinutes ?? 0).toFixed(2)),
      );
      setValue('overrideReason', driverDay.shiftAdjustmentType ?? null);
      setValue('externalId', driverDay.externalId ?? null);
    }
  }, [
    driverDay.totalShiftMinutes,
    driverDay.shiftAdjustmentType,
    driverDay.externalId,
    isEditingShiftSummary,
  ]);

  useEffect(() => {
    // Only update form values when not editing to avoid overwriting user changes
    if (!isEditingCharges) {
      setValue(
        'addOnCharges',
        addOns.map((addOnCharge) => {
          return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
        }),
      );
    }
  }, [addOns, isEditingCharges]);

  useEffect(() => {
    // Only update form values when not editing to avoid overwriting user changes
    if (!isEditingCharges) {
      setValue(
        'resourceUsageLogs',
        resourceUsageLogs.map((resourceUsageLog) => {
          return schemaHelpers.resourceUsageLogToResourceUsageLogDTO(resourceUsageLog);
        }),
      );
    }
  }, [resourceUsageLogs, isEditingCharges]);

  useEffect(() => {
    // Only update form values when not editing to avoid overwriting user changes
    if (!isEditingShiftSummary) {
      setValue('shiftStart', normalizedEffectiveShiftStart);
      setValue('shiftEnd', normalizedEffectiveShiftEnd);
    }
  }, [
    driverDay.adjustedStartedShiftAt,
    driverDay.startedShiftAt,
    driverDay.adjustedEndedShiftAt,
    driverDay.endedShiftAt,
    isEditingShiftSummary,
  ]);

  // Prefill the shift summary from an AI-scanned timesheet: open the card in edit mode
  // with start/end/total populated so the user reviews and saves (never auto-saved).
  const applyExtractedTimes = useCallback(
    (timesheet: Timesheet) => {
      // A locked (non-editable) shift has no Save/Cancel controls, so forcing edit mode
      // would trap the user in editable fields they can neither save nor dismiss. Never
      // prefill a shift that cannot be edited.
      if (!driverDay.editable) {
        return;
      }

      // Anchor the AI times to the shift's own date, not the date the AI read from the
      // image: the driver-day defines which day the shift is on and there is no date input
      // to correct a misread, so only the time-of-day comes from the scan. driverDayDate is
      // always present (unlike the clocked shift start / first job start), so use it.
      const shiftAnchorDate = driverDay.driverDayDate;
      const shiftStart = combineShiftDateAndTime(shiftAnchorDate, timesheet.startedAt);
      let shiftEnd = combineShiftDateAndTime(shiftAnchorDate, timesheet.endedAt);
      // Overnight shift: roll the end to the next day when it lands on/before the start.
      if (shiftStart && shiftEnd && shiftEnd <= shiftStart) {
        shiftEnd = dayjs(shiftEnd).add(1, 'day').toDate();
      }

      const extractedTotalHours = minutesToDecimalHours(timesheet.totalMinutes);
      const hasExtractedTotal = extractedTotalHours !== null;

      if (!shiftStart && !shiftEnd && !hasExtractedTotal) {
        return;
      }

      setIsEditingShiftSummary(true);
      if (shiftStart) {
        setValue('shiftStart', shiftStart, { shouldDirty: true });
      }
      if (shiftEnd) {
        setValue('shiftEnd', shiftEnd, { shouldDirty: true });
      }
      // When both start and end are present the shift-summary effect derives the total
      // from them, so only apply the AI total directly when it can't be derived.
      if ((!shiftStart || !shiftEnd) && hasExtractedTotal) {
        setValue('shiftTimeInHours', extractedTotalHours, { shouldDirty: true });
      }

      toasterStore.push(
        alert(t('approvals.driver_day.ai_shift_times_applied'), AlertTypes.info),
      );
    },
    [setValue, toasterStore, driverDay.driverDayDate, driverDay.editable],
  );

  useEffect(() => {
    if (!companyId || !pendingScanTimesheetId) {
      return;
    }

    let attempts = 0;
    const intervalId = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_SCAN_POLL_ATTEMPTS) {
        clearInterval(intervalId);
        setPendingScanTimesheetId(null);

        return;
      }

      const timesheet = await getTimesheetById(companyId, pendingScanTimesheetId, {
        silent: true,
      });
      if (timesheet?.aiProcessed) {
        clearInterval(intervalId);
        setPendingScanTimesheetId(null);
        // The drawer opens from the record map built at list time, which still holds the
        // pre-scan snapshot; refresh it so the sheet shows its scanned values.
        getTimesheets({ silent: true });
        applyExtractedTimes(timesheet);
      }
    }, SCAN_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [companyId, pendingScanTimesheetId, getTimesheetById, applyExtractedTimes]);

  const isScanningTimesheet = pendingScanTimesheetId !== null;

  const isSavingShiftSummaryRef = useRef(false);
  const [isSavingShiftSummary, setIsSavingShiftSummary] = useState(false);
  const isSavingChargesRef = useRef(false);
  const [isSavingCharges, setIsSavingCharges] = useState(false);

  const onSubmitShiftSummary = async (data: ShiftSummaryFormData) => {
    if (isSavingShiftSummaryRef.current) {
      return;
    }

    isSavingShiftSummaryRef.current = true;
    setIsSavingShiftSummary(true);

    try {
      const result = await updateDriverDay(
        driverDay.id,
        calculateAdjustmentMinutes(
          data.shiftStart ? new Date(data.shiftStart) : null,
          data.shiftEnd ? new Date(data.shiftEnd) : null,
          data.shiftTimeInHours,
        ),
        data.externalId,
        data.shiftStart ? new Date(data.shiftStart).toISOString() : null,
        data.shiftEnd ? new Date(data.shiftEnd).toISOString() : null,
        data.overrideReason ?? undefined,
      );

      if (result.errors) {
        const apiErrors = result.errors;
        if (apiErrors.length === 1) {
          let errorMessage = apiErrors[0].message;
          if (apiErrors[0].field === 'external_id') {
            errorMessage = errorMessage.replace(/External/gi, 'Timesheet ID');
          }
          toasterStore.push(alert(errorMessage, AlertTypes.error));
        } else {
          toasterStore.push(
            alert(
              t('error_messages.driver_days.failed_to_update_driver_day'),
              AlertTypes.error,
            ),
          );
        }
      } else {
        onDriverDayChange(result.driverDay);
        setIsEditingShiftSummary(false);
        toasterStore.push(
          alert(
            t('approvals.driver_pay.shift_summary_updated_successfully'),
            AlertTypes.success,
          ),
        );
      }
    } catch {
      // updateDriverDay already toasted and reported this.
    } finally {
      isSavingShiftSummaryRef.current = false;
      setIsSavingShiftSummary(false);
    }
  };

  const onSubmitCharges = async (data: ChargesFormData) => {
    if (isSavingChargesRef.current) {
      return;
    }

    isSavingChargesRef.current = true;
    setIsSavingCharges(true);

    try {
      const results = await Promise.allSettled([
        batchUpdateResourceUsageLogsByDriverDay(
          driverDay.id,
          data.resourceUsageLogs,
          driverDay.startedShiftAt
            ? driverDay.startedShiftAt.toISOString()
            : firstJobStartAt.toISOString(),
        ),
        batchUpdateAddOnChargesByDriverDay(companyId, driverDay.id, data.addOnCharges),
      ]);
      const rejection = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );

      if (rejection) {
        reportRequestError(
          rejection.reason,
          t('error_messages.driver_days.failed_to_update_driver_day'),
        );

        return;
      }

      setIsEditingCharges(false);
      toasterStore.push(
        alert(
          t('approvals.driver_pay.settlements.add_ons_updated_successfully'),
          AlertTypes.success,
        ),
      );
    } finally {
      isSavingChargesRef.current = false;
      setIsSavingCharges(false);
    }
  };

  const handleApprove = () => {
    driverDayStore.setShiftApprovalLoading(driverDay.id, true);

    // Set jobs waiting for RTU
    driverDayStore.setJobsWaitingForRTU(jobsIds);

    approveDriverDay(companyId, driverDay.id)
      .then((approvedDriverDay) => {
        trackApprovalBatch('driver_day', 1);
        onDriverDayChange(approvedDriverDay);
      })
      .finally(() => {
        driverDayStore.setShiftApprovalLoading(driverDay.id, false);
        refetchApprovals();
      });
  };

  const handleUnapprove = () => {
    if (!driverDay.unapprovable) return undefined;
    driverDayStore.setShiftApprovalLoading(driverDay.id, true);

    // Set jobs waiting for RTU
    driverDayStore.setJobsWaitingForRTU(jobsIds);

    unapproveDriverDay(companyId, driverDay.id)
      .then(onDriverDayChange)
      .finally(() => {
        driverDayStore.setShiftApprovalLoading(driverDay.id, false);
        refetchApprovals();
      });
  };

  const handleEndShift = () => {
    setIsLoadingDriverDay(true);
    setDriverStateToOffDuty(driverDay.driver.id)
      .then(() => {
        getDriverDayById(driverDay.id).then(onDriverDayChange);
        setIsLoadingDriverDay(false);
      })
      .catch((e) => {
        toasterStore.push(alert(e.message, AlertTypes.error));
      });
  };

  const ApprovalCTA = () => {
    const {
      isApproved: approved,
      isApprovable: approvable,
      isUnapprovable: unapprovable,
    } = getApprovalStatus(driverDay, userPermissions, userPermissions.canApproveShift);
    const isUpdatingApprovalStatus = driverDayStore.isShiftApprovalLoading(driverDay.id);

    if (approved) {
      if (unapprovable) {
        return (
          <ApprovalActionsMenu
            onMenuItemClick={handleUnapprove}
            approved={approved}
            isLoading={isUpdatingApprovalStatus}
          />
        );
      }

      return <ApprovedChip hideDropdownArrow />;
    }

    if (!driverDay.endedShiftAt && driverDay.startedShiftAt) {
      return (
        <Button
          disabled={!driverCanBeSetOffDuty}
          loading={isLoadingDriverDay}
          variant="contained"
          color="error"
          onClick={handleEndShift}
        >
          {t('actions.end_shift')}
        </Button>
      );
    }

    if (approvable && !approved) {
      return (
        <Button
          startIcon={<CheckIcon />}
          variant="contained"
          color="success"
          onClick={() => {
            handleApprove();
          }}
          sx={{
            backgroundColor: theme.brandV2.colors.treadGreen,
          }}
          size="small"
          disabled={isEditingShiftSummary || isEditingCharges}
          loading={isUpdatingApprovalStatus}
        >
          {t('actions.approve_shift')}
        </Button>
      );
    }

    return <Chip color="secondary" label={t('status.pending')} hideDropdownArrow />;
  };

  const EditShiftSummaryCTA = () => {
    if (!driverDay.editable) {
      return null;
    }

    return isEditingShiftSummary ? (
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
        }}
      >
        <Button
          onClick={() => {
            if (isSavingShiftSummaryRef.current) {
              return;
            }

            setIsEditingShiftSummary(false);
          }}
          variant="outlined"
          color="secondary"
          sx={{
            fontWeight: 600,
            '&.MuiButton-root': {
              borderColor: theme.brandV2.colors.treadGray7,
            },
          }}
          startIcon={<Close />}
          disabled={isSavingShiftSummary}
        >
          {t('actions.cancel')}
        </Button>

        <Button
          onClick={handleSubmit(onSubmitShiftSummary)}
          variant={'contained'}
          color={'brandV2Green'}
          sx={{
            fontWeight: 600,
            '&.MuiButton-root': {
              borderColor: theme.brandV2.colors.treadGray7,
            },
          }}
          startIcon={<Check />}
          loading={isSavingShiftSummary}
          disabled={!isValid || !isDirty}
        >
          {t('actions.save_changes')}
        </Button>
      </Box>
    ) : (
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => {
          setIsEditingShiftSummary(true);
        }}
        sx={{
          fontWeight: 600,
          '&.MuiButton-root': {
            borderColor: theme.brandV2.colors.treadGray7,
          },
        }}
        startIcon={<Edit />}
        disabled={driverDayStore.isShiftApprovalLoading(driverDay.id)}
      >
        {t('actions.edit')}
      </Button>
    );
  };

  const EditChargesCTA = () => {
    if (!driverDay.editable) {
      return null;
    }

    return isEditingCharges ? (
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
        }}
      >
        <Button
          onClick={() => {
            if (isSavingChargesRef.current) {
              return;
            }

            setIsEditingCharges(false);
          }}
          variant="outlined"
          color="secondary"
          sx={{
            fontWeight: 600,
            '&.MuiButton-root': {
              borderColor: theme.brandV2.colors.treadGray7,
            },
          }}
          startIcon={<Close />}
          disabled={isSavingCharges}
        >
          {t('actions.cancel')}
        </Button>

        <Button
          onClick={handleSubmit(onSubmitCharges)}
          variant={'contained'}
          color={'brandV2Green'}
          sx={{
            fontWeight: 600,
            '&.MuiButton-root': {
              borderColor: theme.brandV2.colors.treadGray7,
            },
          }}
          startIcon={<Check />}
          loading={isSavingCharges}
          disabled={!isValid || !isDirty}
        >
          {t('actions.save_changes')}
        </Button>
      </Box>
    ) : (
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => {
          setIsEditingCharges(true);
        }}
        sx={{
          fontWeight: 600,
          '&.MuiButton-root': {
            borderColor: theme.brandV2.colors.treadGray7,
          },
        }}
        startIcon={<Edit />}
        disabled={driverDayStore.isShiftApprovalLoading(driverDay.id)}
      >
        {t('actions.edit')}
      </Button>
    );
  };

  const ActionsSection = () => {
    if (isEditingShiftSummary) return <EditShiftSummaryCTA />;
    else {
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
          }}
        >
          <EditShiftSummaryCTA />
          <ApprovalCTA />
        </Box>
      );
    }
  };

  const ChargesActionsSection = () => {
    return <EditChargesCTA />;
  };

  const AddTimeSheetButton = () => {
    // Creating a timesheet via the new model requires create_ticket; don't offer it otherwise.
    if (useNewTimesheet && !userPermissions.canCreateTicket) {
      return null;
    }

    return isTimesheetLoading ? (
      <Button
        loading={true}
        loadingPosition="start"
        startIcon={<></>}
        variant="text"
        color="brandV2OrangeDark"
        sx={{
          fontSize: '0.75rem',
          height: 'fit-content',
          minHeight: '21px',
          minWidth: '0px',
          pt: 1,
          width: 'fit-content',
        }}
      />
    ) : (
      <Button
        variant="text"
        color="brandV2OrangeDark"
        sx={{
          height: 'fit-content',
          width: 'fit-content',
          p: 0,
          fontSize: '0.75rem',
          minWidth: '0px',
        }}
        onClick={() => fileInputRef && fileInputRef.current?.click()}
      >
        {t('actions.add_sheet')}
      </Button>
    );
  };

  const ViewTimeSheetButton = () => {
    const sheets = showAllList ? allTimesheets : allTimesheets.slice(0, 3);
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'hidden' }}
      >
        {sheets.map((timesheet) => {
          // New-model rows delete via the ticket endpoint (needs delete_ticket); legacy rows use
          // the file-attachment endpoint and rely on the record's own `deletable`.
          const canDeleteItem =
            timesheet.source === 'timesheet'
              ? userPermissions.canDeleteTicket
              : timesheet.deletable === true;

          return (
            <Box
              key={timesheet.key}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                maxWidth: isEditingShiftSummary ? '100%' : '70%',
                gap: 0.5,
              }}
            >
              <PseudoLink
                title={
                  timesheet.timesheetNumber ||
                  timesheet.fileName ||
                  t('actions.view_sheet')
                }
                action={() => {
                  const fullTimesheet =
                    useNewTimesheet && timesheet.source === 'timesheet'
                      ? timesheetsByKeyRef.current.get(timesheet.key)
                      : undefined;

                  if (fullTimesheet) {
                    setDrawerTimesheet(fullTimesheet);
                  } else if (timesheet.fileUrl) {
                    // Legacy file-attachment rows have no Timesheet record; open the image.
                    window.open(timesheet.fileUrl);
                  }
                }}
                sx={{
                  maxWidth: isEditingShiftSummary ? '100%' : '230px',
                  p: 0,
                  fontSize: '0.75rem',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
                underline={false}
                linkColor={theme.brandV2.colors.treadBlue}
              />
              {isEditingShiftSummary ? (
                canDeleteItem ? (
                  <DeleteOutline
                    onClick={() => {
                      setToBeDeletedItem(timesheet);
                      setIsTimesheetDeleteDialogOpen(true);
                    }}
                    sx={{ cursor: 'pointer' }}
                    fontSize="small"
                  />
                ) : (
                  <DeleteOutline
                    fontSize="small"
                    sx={{
                      color: theme.palette.action.disabled,
                      opacity: 0.38,
                    }}
                  />
                )
              ) : null}
            </Box>
          );
        })}
        {allTimesheets.length > 3 ? (
          <Button
            variant="text"
            color="brandV2OrangeDark"
            sx={{
              height: 'fit-content',
              width: 'fit-content',
              p: 0,
              fontSize: '0.75rem',
              minWidth: 'unset',
            }}
            onClick={() => setShowAllList(!showAllList)}
          >
            {showAllList ? t('actions.show_less') : t('actions.show_more')}
          </Button>
        ) : null}
        <AddTimeSheetButton />
      </Box>
    );
  };

  const updateFileAttachment = (id: string) => {
    updateFileAttachmentById(id, {
      category: FileAttachmentCategory.TIMESHEET,
    })
      .then(() => {
        toasterStore.push(
          alert(
            t('approvals.driver_day.time_sheet_added_successfully'),
            AlertTypes.success,
          ),
        );
        getTimesheets();
      })
      .finally(() => {
        setIsTimesheetLoading(false);
      });
  };

  const handleCreateFileAttachment = async (file: File) => {
    if (!file) return;
    if (file.size / 1024 / 1024 > maxFileSize) {
      alert(
        t('file_upload.max_size', { maxSize: maxFileSize + ' MB' }),
        AlertTypes.error,
      );
      return;
    }

    setIsTimesheetLoading(true);

    if (useNewTimesheet && companyId) {
      try {
        const created = await createTimesheet(companyId, {
          file,
          driverDayId: driverDay.id,
        });
        toasterStore.push(
          alert(
            t('approvals.driver_day.time_sheet_added_successfully'),
            AlertTypes.success,
          ),
        );
        await getTimesheets();
        // Prefill the shift from the AI extraction once it is scanned; poll if it is
        // still processing (extraction runs asynchronously on the backend). Only for
        // editable shifts — a locked shift has no Save/Cancel, so we neither force edit
        // mode nor show the scanning overlay (which would resolve into a no-op).
        if (driverDay.editable) {
          if (created?.aiProcessed) {
            applyExtractedTimes(created);
          } else if (created) {
            setPendingScanTimesheetId(created.id);
          }
        }
      } finally {
        setIsTimesheetLoading(false);
      }

      return;
    }

    const attachment = await createFileAttachmentOnDriverDay(file, driverDay.id);
    if (attachment) {
      updateFileAttachment(attachment.id);
    }
  };

  const handleDeleteTimesheetItem = async (item: TimesheetListItem | null) => {
    if (!item) return;

    setIsTimesheetDeleting(true);
    try {
      if (item.source === 'timesheet' && companyId) {
        await deleteTimesheetById(companyId, item.deleteId);
      } else {
        await deleteFileAttachmentById(item.deleteId);
      }
      await getTimesheets();
      setIsTimesheetDeleteDialogOpen(false);
      setToBeDeletedItem(null);
      toasterStore.push(
        alert(
          t('approvals.driver_day.time_sheet_removed_successfully'),
          AlertTypes.success,
        ),
      );
    } finally {
      setIsTimesheetDeleting(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Box
        component="div"
        id="shift-details-form"
        aria-label="shift details"
        sx={{
          backgroundColor: theme.semantic.surfaceRaised,

          borderRadius: 2,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          height: 'fit-content',
          gap: 1.5,
        }}
      >
        <Box
          aria-label="shift summary"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 3,
            p: 1.5,
            position: 'relative',
          }}
        >
          {isScanningTimesheet && <ScanningOverlay />}
          <Box sx={{ flex: 1, display: 'grid', alignContent: 'start' }}>
            <Typography
              variant="body2"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2 }}
            >
              {t('approvals.driver_day.shift_start_details')}
            </Typography>
            {isEditingShiftSummary ? (
              <NewTimeFormField
                control={control}
                errors={errors}
                name={'shiftStart'}
                sx={{ mb: 0.5 }}
              />
            ) : (
              <Typography variant="h6">
                {effectiveShiftStart
                  ? dateFormat(effectiveShiftStart, 'hh:mm A')
                  : emDash}
              </Typography>
            )}

            {displayShiftStart && (
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => theme.brandV2.colors.treadGray2,
                  fontWeight: 500,
                }}
              >
                {dateFormat(displayShiftStart, 'ddd MMM-DD-YYYY')}
              </Typography>
            )}

            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2, fontWeight: 500 }}
            >
              {t('approvals.driver_day.first_geofence_entry')}:{' '}
              {driverDay?.firstGeofenceEntryAt
                ? dateFormat(driverDay?.firstGeofenceEntryAt, 'hh:mm A')
                : emDash}
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2, fontWeight: 500 }}
            >
              {t('approvals.driver_day.first_ticket')}:{' '}
              {driverDay?.firstTicketAt
                ? dateFormat(driverDay?.firstTicketAt, 'hh:mm A')
                : emDash}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: 'grid', alignContent: 'start' }}>
            <Typography
              variant="body2"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2 }}
            >
              {t('approvals.driver_day.shift_end_details')}
            </Typography>
            {isEditingShiftSummary ? (
              <NewTimeFormField
                control={control}
                errors={errors}
                name={'shiftEnd'}
                sx={{ mb: 0.5 }}
              />
            ) : (
              <Typography variant="h6">
                {effectiveShiftEnd ? dateFormat(effectiveShiftEnd, 'hh:mm A') : emDash}
              </Typography>
            )}

            {displayShiftEnd && (
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => theme.brandV2.colors.treadGray2,
                  fontWeight: 500,
                }}
              >
                {dateFormat(displayShiftEnd, 'ddd MMM-DD-YYYY')}
              </Typography>
            )}

            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2, fontWeight: 500 }}
            >
              {t('approvals.driver_day.last_geofence_entry')}:{' '}
              {driverDay?.lastGeofenceExitAt
                ? dateFormat(driverDay?.lastGeofenceExitAt, 'hh:mm A')
                : emDash}
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2, fontWeight: 500 }}
            >
              {t('approvals.driver_day.last_ticket')}:{' '}
              {driverDay?.lastTicketAt
                ? dateFormat(driverDay?.lastTicketAt, 'hh:mm A')
                : emDash}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: 'grid', alignContent: 'start' }}>
            <Typography
              variant="body2"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2 }}
            >
              {t('approvals.driver_day.total_hours')}
            </Typography>
            {isEditingShiftSummary ? (
              <TextFormField
                control={control}
                errors={errors}
                name={'shiftTimeInHours'}
              />
            ) : (
              <Typography variant="h6">
                {driverDay.totalShiftMinutes
                  ? formatMinutesToHoursString(driverDay.totalShiftMinutes)
                  : emDash}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2, fontWeight: 500 }}
            >
              {`${t('approvals.driver_day.breaks')}: ${driverDay?.totalBreakMinutes ? `${driverDay?.totalBreakMinutes}min` : emDash}`}
            </Typography>
          </Box>

          <Box
            sx={{ alignContent: 'start', display: 'grid', flex: 1, overflow: 'hidden' }}
          >
            <Typography
              variant="body2"
              sx={{ color: (theme) => theme.brandV2.colors.treadGray2 }}
            >
              {t('approvals.driver_day.timesheet_id')}
            </Typography>

            <input
              type="file"
              accept="image/*"
              maxLength={1}
              hidden
              ref={fileInputRef}
              onChange={(e) => {
                if (e.target.files?.length)
                  handleCreateFileAttachment(e.target.files?.[0]);
              }}
              onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                const element = event.target as HTMLInputElement;
                element.value = '';
              }}
            />
            {isEditingShiftSummary ? (
              <>
                <TextFormField name={'externalId'} errors={errors} control={control} />

                {!allTimesheets.length ? (
                  <AddTimeSheetButton />
                ) : (
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      overflow: 'hidden',
                    }}
                  >
                    <ViewTimeSheetButton />
                  </Box>
                )}
              </>
            ) : (
              <>
                <Typography variant="h6">{driverDay.externalId ?? emDash}</Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (theme) => theme.brandV2.colors.treadGray2,
                    fontWeight: 500,
                  }}
                >
                  {driverDay.driverDayId}
                </Typography>

                {allTimesheets.length ? <ViewTimeSheetButton /> : <AddTimeSheetButton />}
              </>
            )}
          </Box>
          <ActionsSection />
        </Box>

        <ConfirmationDialog
          confirmButtonProps={{ children: t('actions.remove_time_sheet') }}
          isBusy={isTimesheetDeleting}
          isOpen={isTimesheetDeleteDialogOpen}
          onClose={() => {
            setToBeDeletedItem(null);
            setIsTimesheetDeleteDialogOpen(false);
          }}
          onConfirm={() => {
            handleDeleteTimesheetItem(toBeDeletedItem);
          }}
          title={t('actions.remove_time_sheet')}
        >
          <Typography>{t('approvals.driver_day.confirm_removing_time_sheet')}</Typography>
        </ConfirmationDialog>

        <TimesheetDrawer
          companyId={companyId}
          onClose={() => {
            setDrawerTimesheet(null);
            // Re-fetch silently so a save/delete made inside the drawer is reflected without
            // flashing the list empty when nothing changed.
            getTimesheets({ silent: true });
          }}
          open={Boolean(drawerTimesheet)}
          timesheet={drawerTimesheet}
        />

        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: hasCharges ? 'space-between' : 'flex-end',
                px: 1.5,
              }}
            >
              {hasCharges ? (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {t('approvals.summary')}
                </Typography>
              ) : null}
              <ChargesActionsSection />
            </Box>
          </Box>

          <ChargesTable
            isEditing={isEditingCharges}
            addOns={addOns}
            resourceUsageLogs={resourceUsageLogs}
            phasesOptions={phasesOptions}
            driver={driverDay.driver}
          />
        </Box>

        {driverDay.notes && (
          <Box sx={{ px: 1.5, pb: 1.5, mt: 1.5 }}>
            <Typography
              sx={{
                color: theme.brandV2.colors.treadBlack,
                fontWeight: 600,
              }}
            >
              {t('approvals.driver_day.log_notes')}
            </Typography>

            <Box
              sx={{
                mt: 1.5,
                backgroundColor: theme.brandV2.colors.treadGray8,
                borderRadius: theme.spacing(0.5),
                p: 1,
              }}
            >
              <Typography
                variant="body2"
                color={theme.brandV2.colors.treadGray1}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {driverDay.notes}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </FormProvider>
  );
});
